// SAFE-DRAFT: the unsaved part of a handover draft, kept on this device until the server confirms
// it (DR-180, DR-190). The server stays the record: the editor saves automatically within seconds;
// this copy only bridges an expired sign-in (the redirect to the identity provider), a refresh, a
// network outage, or a tab or browser closed before the save (a phone closes background tabs).
// It is kept in localStorage, per user and handover, for at most 72 hours; sign-out removes the
// user's copies only after asking. This is not offline work: nothing is sent or signed without the
// server. It holds the draft fields and photo ids only: never tokens, image files, or contractor
// contacts.
// A finding still being written in its dialog (not yet added to the draft) is kept the same way,
// so an expired sign-in or a refresh does not lose what the inspector typed (Gate 2A).
import type { HandoverDocument } from '@sivan/shared';
import type { Draft, FindingFields } from './handover-draft';

const PREFIX = 'sivan:handover-draft:v2:';
/** Copies kept by the earlier version, in the tab only (moved to the device on first use). */
const TAB_PREFIX = 'sivan:handover-draft:v1:';
/** Older copies are dropped (a forgotten device must not resurrect stale work). */
const MAX_AGE_MS = 72 * 60 * 60 * 1000;

export interface StoredDraft {
  readonly version: 1;
  /** The signed-in user who made the changes (null with the development identity). */
  readonly userId: string | null;
  /** The document the changes were made on (to merge them onto a newer one). */
  readonly base: HandoverDocument;
  readonly draft: Draft;
  readonly savedAt: number;
}

let currentUser: string | null = null;
/** Set by the sign-in gate: drafts are kept and restored per user. */
export const setDraftUser = (userId: string | null): void => {
  currentUser = userId;
};

let migrated = false;
/** Moves the copies the earlier version kept in this tab onto the device (once per page). */
function moveTabCopies(device: Storage): void {
  if (migrated) return;
  migrated = true;
  try {
    const tab = window.sessionStorage;
    const keys: string[] = [];
    for (let i = 0; i < tab.length; i += 1) {
      const key = tab.key(i);
      if (key?.startsWith(TAB_PREFIX)) keys.push(key);
    }
    for (const key of keys) {
      const text = tab.getItem(key);
      const rest = key.slice(TAB_PREFIX.length);
      if (text !== null) {
        const entry = JSON.parse(text) as { userId?: string | null };
        const target = `${PREFIX}${entry.userId ?? '-'}:${rest}`;
        if (device.getItem(target) === null) device.setItem(target, text);
      }
      tab.removeItem(key);
    }
  } catch {
    // Unreadable or unavailable: nothing to move.
  }
}

const storage = (): Storage | null => {
  try {
    const device = window.localStorage;
    moveTabCopies(device);
    return device;
  } catch {
    return null;
  }
};
/** One copy per user and handover: users sharing a device never overwrite each other's. */
const userPart = () => `${PREFIX}${currentUser ?? '-'}:`;
const keyOf = (handoverId: string) => `${userPart()}${handoverId}`;

/** The document without contractor contacts (not needed to merge, not kept in the browser). */
function withoutContacts(doc: HandoverDocument): HandoverDocument {
  return {
    ...doc,
    findings: doc.findings.map((f) => ({
      ...f,
      contractorContact: null,
      contractorContactRecorded: null,
    })),
  };
}

export function keepDraft(base: HandoverDocument, draft: Draft, now = Date.now()): void {
  const entry: StoredDraft = {
    version: 1,
    userId: currentUser,
    base: withoutContacts(base),
    draft,
    savedAt: now,
  };
  try {
    storage()?.setItem(keyOf(base.id), JSON.stringify(entry));
  } catch {
    // Storage full or unavailable: the editor still saves to the server as usual.
  }
}

export function forgetDraft(handoverId: string): void {
  storage()?.removeItem(keyOf(handoverId));
}

/** The kept draft of this handover for the current user, if recent (an old or broken one is removed). */
export function findDraft(handoverId: string, now = Date.now()): StoredDraft | null {
  const store = storage();
  const text = store?.getItem(keyOf(handoverId)) ?? null;
  if (text === null) return null;
  try {
    // Written by this code, but read defensively (another version may have written it).
    const entry = JSON.parse(text) as Partial<StoredDraft> & Pick<StoredDraft, 'base'>;
    if (
      entry.version !== 1 ||
      entry.draft === undefined ||
      entry.savedAt === undefined ||
      entry.base.id !== handoverId ||
      entry.userId !== currentUser ||
      now - entry.savedAt > MAX_AGE_MS
    ) {
      forgetDraft(handoverId);
      return null;
    }
    return {
      ...entry,
      version: 1,
      draft: entry.draft,
      savedAt: entry.savedAt,
      userId: entry.userId ?? null,
    };
  } catch {
    forgetDraft(handoverId);
    return null;
  }
}

/**
 * True when handover changes are kept on this device: the current user's, or before sign-in
 * anyone's (shown on the sign-in screen and before sign-out).
 */
export function hasKeptDrafts(): boolean {
  const store = storage();
  if (store === null) return false;
  const prefix = currentUser === null ? PREFIX : userPart();
  for (let i = 0; i < store.length; i += 1) if (store.key(i)?.startsWith(prefix)) return true;
  return false;
}

/** Removes the current user's kept drafts (on sign-out, after the user confirmed it). */
export function forgetAllDrafts(): void {
  const store = storage();
  if (store === null) return;
  const prefix = userPart();
  const keys: string[] = [];
  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i);
    if (key?.startsWith(prefix)) keys.push(key);
  }
  for (const key of keys) store.removeItem(key);
}

/** A finding being written in its dialog: what was typed, and which finding or item it is for. */
export interface OpenFinding {
  /** `add`: a new finding (on `itemId`, or free); `edit`: the finding with `key`. */
  readonly mode: 'add' | 'edit';
  readonly key: string | null;
  readonly itemId: string | null;
  readonly fields: FindingFields;
}

interface StoredOpenFinding extends OpenFinding {
  readonly version: 1;
  readonly userId: string | null;
  readonly savedAt: number;
}

const openKeyOf = (handoverId: string) => `${userPart()}${handoverId}:finding`;

export function keepOpenFinding(handoverId: string, open: OpenFinding, now = Date.now()): void {
  const entry: StoredOpenFinding = { version: 1, userId: currentUser, savedAt: now, ...open };
  try {
    storage()?.setItem(openKeyOf(handoverId), JSON.stringify(entry));
  } catch {
    // Storage full or unavailable: the dialog still works; only the bridge is missing.
  }
}

export function forgetOpenFinding(handoverId: string): void {
  storage()?.removeItem(openKeyOf(handoverId));
}

/** The finding left open in its dialog for this handover and user, if recent; others are removed. */
export function findOpenFinding(handoverId: string, now = Date.now()): OpenFinding | null {
  const text = storage()?.getItem(openKeyOf(handoverId)) ?? null;
  if (text === null) return null;
  try {
    const entry = JSON.parse(text) as Partial<StoredOpenFinding>;
    if (
      entry.version !== 1 ||
      (entry.mode !== 'add' && entry.mode !== 'edit') ||
      entry.fields === undefined ||
      entry.savedAt === undefined ||
      entry.userId !== currentUser ||
      now - entry.savedAt > MAX_AGE_MS
    ) {
      forgetOpenFinding(handoverId);
      return null;
    }
    return {
      mode: entry.mode,
      key: entry.key ?? null,
      itemId: entry.itemId ?? null,
      fields: entry.fields,
    };
  } catch {
    forgetOpenFinding(handoverId);
    return null;
  }
}
